import { PinState } from './mapData';
import { MAP_STATE_COLORS } from './stateColors';

const SIZE = 28;
const CENTER = SIZE / 2;

/**
 * The pin: a circle with the planned sequence number. Executed is filled with
 * the route color; an incident is white with a red border; a pending one is
 * white with a dashed grey border.
 */
export const pinSvg = (
  color: string,
  state: PinState,
  number: number
): string => {
  const font = number > 99 ? 9 : number > 9 ? 11 : 12;
  const circle =
    state === 'executed'
      ? `<circle cx="${CENTER}" cy="${CENTER}" r="11" fill="${color}" stroke="#fff" stroke-width="2"/>`
      : state === 'incident'
      ? `<circle cx="${CENTER}" cy="${CENTER}" r="11" fill="#fff" stroke="${MAP_STATE_COLORS.incident}" stroke-width="2.5"/>`
      : `<circle cx="${CENTER}" cy="${CENTER}" r="11" fill="#fff" stroke="${MAP_STATE_COLORS.pending}" stroke-width="1.8" stroke-dasharray="3 2.5"/>`;
  const textFill = state === 'executed' ? '#fff' : 'rgba(0,0,0,0.75)';

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${SIZE}" height="${SIZE}" viewBox="0 0 ${SIZE} ${SIZE}">` +
    circle +
    `<text x="${CENTER}" y="${
      CENTER + 4
    }" text-anchor="middle" font-family="Arial,sans-serif" font-size="${font}" font-weight="700" fill="${textFill}">${number}</text>` +
    `</svg>`
  );
};

const dataUri = (svg: string) =>
  `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;

const cache = new Map<string, google.maps.Icon>();

/**
 * Marker icon for (color, state, number). Memoized: a thousand stops repeat a
 * few hundred combinations, and reusing the object keeps the Maps API from
 * re-rasterizing an unchanged icon on every render.
 */
export const pinIcon = (
  color: string,
  state: PinState,
  number: number
): google.maps.Icon => {
  const key = `${color}|${state}|${number}`;
  const hit = cache.get(key);
  if (hit) return hit;

  const icon: google.maps.Icon = {
    url: dataUri(pinSvg(color, state, number)),
    scaledSize: new google.maps.Size(SIZE, SIZE),
    anchor: new google.maps.Point(CENTER, CENTER),
  };
  cache.set(key, icon);

  return icon;
};

const CLUSTER_SIZES = [40, 48, 56];

const clusterSvg = (size: number) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><circle cx="${
    size / 2
  }" cy="${size / 2}" r="${
    size / 2 - 3
  }" fill="#434343" fill-opacity="0.85" stroke="#fff" stroke-width="3"/></svg>`;

/** Cluster bubbles as data URIs, so no image is fetched from a CDN. */
export const CLUSTER_STYLES = CLUSTER_SIZES.map((size) => ({
  url: dataUri(clusterSvg(size)),
  height: size,
  width: size,
  textColor: '#fff',
  textSize: 13,
}));
